"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { getOrCreateDeviceId } from "../utility/device-id";

function useRestaurantWebSocket(config) {
  const {
    url,
    bunitId,
    tillId,
    enabled = false,
    reconnectInterval = 3000,
    maxReconnectAttempts = Number.POSITIVE_INFINITY,
    heartbeatInterval = 5000,
    debug = false,
    onMessage: customOnMessage,
    onConnect: customOnConnect,
    onDisconnect: customOnDisconnect,
    onError: customOnError,
  } = config;

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const [deviceId] = useState(() => getOrCreateDeviceId());
  const [orderQueue, setOrderQueue] = useState([]);
  const [visibleOrders, setVisibleOrders] = useState([]);
  const [orderUpdateFlag, setOrderUpdateFlag] = useState(0);

  const MAX_VISIBLE_NOTIFICATIONS = 1;

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const lastStreamIdRef = useRef("0");
  const connectionAttemptRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const isConnectingRef = useRef(false);
  const processedTableIdsRef = useRef(new Set());
  const lastPongTimeRef = useRef(Date.now());
  const callbacksRef = useRef({
    onMessage: null,
    onConnect: null,
    onDisconnect: null,
    onError: null,
  });
  const eventListenersRef = useRef({});

  useEffect(() => {
    callbacksRef.current = {
      onMessage: customOnMessage,
      onConnect: customOnConnect,
      onDisconnect: customOnDisconnect,
      onError: customOnError,
    };
  }, [customOnMessage, customOnConnect, customOnDisconnect, customOnError]);

  const log = useCallback(
    (...args) => {
      if (debug) {
        console.log("[v0] [RestaurantWS]", ...args);
      }
    },
    [debug]
  );

  const subscribe = useCallback((eventType, callback) => {
    if (!eventListenersRef.current[eventType]) {
      eventListenersRef.current[eventType] = new Set();
    }

    eventListenersRef.current[eventType].add(callback);

    return () => {
      eventListenersRef.current[eventType]?.delete(callback);
    };
  }, []);

  const emitEvent = useCallback((eventType, payload) => {
    const listeners = eventListenersRef.current[eventType];

    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(payload);
        } catch (err) {
          console.error("[RestaurantWS] Event listener error:", err);
        }
      });
    }
  }, []);

  const normalizeEvent = useCallback((message) => {
    // IMPORTANT FIX
    const eventType = message.type || message.event;

    switch (eventType) {
      // case "NEW_KOT":
      // case "NEW_ORDER":
      case "order:new":
        return {
          event: "order:new",

          payload: {
            ...message.data,

            orderId: message.data?.orderId || message.data?.fbOrderId || message.data?.order_id,

            externalId: message.data?.externalId || message.data?.fbOrderId || message.data?.order_id,
          },
        };

      // case "ORDER_UPSERT":
      case "order:update":
        return {
          event: "order:update",

          payload: {
            ...message.data,

            orderId: message.data?.orderId || message.data?.fbOrderId || message.data?.order_id,
          },
        };

      default:
        return {
          event: eventType,
          payload: message.data,
        };
    }
  }, []);

  const loadLastStreamId = useCallback(() => {
    try {
      const stored = localStorage.getItem(`restaurant_stream_${bunitId}_${deviceId}`);
      if (stored && stored !== "0") {
        lastStreamIdRef.current = stored;
        log(`Loaded last streamId: ${stored} for device: ${deviceId}`);
      }
    } catch (err) {
      console.error("[v0] Error loading last stream ID:", err);
    }
  }, [bunitId, deviceId, log]);

  const saveStreamId = useCallback(
    (streamId) => {
      if (streamId && streamId !== "0") {
        try {
          localStorage.setItem(`restaurant_stream_${bunitId}_${deviceId}`, streamId);
          lastStreamIdRef.current = streamId;
          log(`Saved streamId: ${streamId} for device: ${deviceId}`);
        } catch (err) {
          console.error("[v0] Error saving stream ID:", err);
        }
      }
    },
    [bunitId, deviceId, log]
  );

  const clearConnectionTimeout = useCallback(() => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  const clearHeartbeatTimeout = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    lastPongTimeRef.current = Date.now();

    heartbeatIntervalRef.current = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: "PING" }));
          log("Heartbeat sent (PING)");

          clearHeartbeatTimeout();
          heartbeatTimeoutRef.current = setTimeout(() => {
            const timeSinceLastPong = Date.now() - lastPongTimeRef.current;
            log(`Heartbeat timeout - no PONG received for ${timeSinceLastPong}ms, forcing reconnect`);
            if (ws) {
              ws.close();
            }
          }, heartbeatInterval * 5);
        } catch (err) {
          console.error("[v0] Error sending heartbeat:", err);
        }
      }
    }, heartbeatInterval);
  }, [heartbeatInterval, log, clearHeartbeatTimeout]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    clearHeartbeatTimeout();
  }, [clearHeartbeatTimeout]);

  const processMessageQueue = useCallback(() => {
    while (messageQueueRef.current.length > 0) {
      const message = messageQueueRef.current.shift();
      const ws = wsRef.current;
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(message));
          log("Queued message sent:", message);
        } else {
          messageQueueRef.current.unshift(message);
          break;
        }
      } catch (err) {
        console.error("[v0] Error sending queued message:", err);
        messageQueueRef.current.unshift(message);
        break;
      }
    }
  }, [log]);

  const sendMessage = useCallback(
    (data) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify(data));
          log("Message sent:", data);
          return true;
        } catch (err) {
          console.error("[v0] Error sending message:", err);
          messageQueueRef.current.push(data);
          return false;
        }
      } else {
        log("WebSocket not open, queueing message:", data);
        messageQueueRef.current.push(data);
        return false;
      }
    },
    [log]
  );

  const requestMissedEvents = useCallback(() => {
    const lastStreamId = lastStreamIdRef.current;

    if (!lastStreamId || lastStreamId === "0") {
      log("Skipping missed events - new connection (streamId is 0)");
      return;
    }

    log(`Requesting missed events since streamId: ${lastStreamId} for device: ${deviceId}`);
    sendMessage({
      type: "GET_MISSED_EVENTS",
      bunitId: bunitId,
      lastStreamId: lastStreamId,
      deviceId: deviceId,
    });
  }, [bunitId, deviceId, sendMessage, log]);

  const connectInternal = useCallback(() => {
    if (isConnectingRef.current) {
      log("Already connecting, skipping...");
      return;
    }

    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        log("Already connected or connecting, skipping...");
        return;
      }
    }

    if (!bunitId || !tillId) {
      console.error("[RestaurantWS] Cannot connect: bunitId and tillId are required");
      return;
    }

    isConnectingRef.current = true;
    connectionAttemptRef.current += 1;

    log("Connecting to WebSocket:", url, "with bunitId:", bunitId);

    try {
      const websocket = new WebSocket(url);

      connectionTimeoutRef.current = setTimeout(() => {
        if (websocket.readyState !== WebSocket.OPEN) {
          console.error("[v0] Connection timeout");
          isConnectingRef.current = false;
          websocket.close();
        }
      }, 10000);

      websocket.onopen = () => {
        log("WebSocket connection established");

        clearConnectionTimeout();

        setConnected(true);
        setReconnecting(false);
        setReconnectAttempts(0);
        setError(null);
        connectionAttemptRef.current = 0;
        isConnectingRef.current = false;
        lastPongTimeRef.current = Date.now();

        const subscribeMsg = {
          type: "SUBSCRIBE",
          subscriptionType: "restaurant",

          bunitId: bunitId,
          tillId: tillId,

          storeId: bunitId,

          lastStreamId: lastStreamIdRef.current,
          deviceId: deviceId,
        };

        log("Subscribing to restaurant updates:", subscribeMsg);
        sendMessage(subscribeMsg);

        if (lastStreamIdRef.current && lastStreamIdRef.current !== "0") {
          setTimeout(() => {
            requestMissedEvents();
          }, 200);
        } else {
          log("New connection - skipping missed events request");
        }

        processMessageQueue();
        startHeartbeat();

        if (callbacksRef.current.onConnect) {
          callbacksRef.current.onConnect();
        }
      };

      websocket.onmessage = (event) => {
        console.log("[v0] [RestaurantWS] Raw message received:", event.data);

        try {
          const message = JSON.parse(event.data);
          console.log("[v0] [RestaurantWS] Parsed message:", message);
          console.log("[v0] [RestaurantWS] Message type:", message.type || message.event);

          if (message.type === "PONG") {
            log("Heartbeat acknowledged");
            lastPongTimeRef.current = Date.now();
            clearHeartbeatTimeout();
            return;
          }

          if (message.type === "SUBSCRIBED") {
            console.log("[v0] [RestaurantWS] Successfully subscribed to restaurant updates:", {
              bunitId: message.bunitId,
            });
            return;
          }

          if (message.type === "REDIS_RECONNECTED") {
            console.log("[v0] [RestaurantWS] Redis reconnected on server - requesting missed events");
            requestMissedEvents();
            return;
          }

          if (message.type === "MISSED_EVENTS_COMPLETE") {
            console.log(`[v0] [RestaurantWS] Missed events sync complete. Total: ${message.count}`);
            return;
          }

          if (message.streamId) {
            console.log("[v0] [RestaurantWS] Saving streamId:", message.streamId);
            saveStreamId(message.streamId);
          }

          if (message.data?.fbTableId) {
            const sessionKey = `${message.data.fbTableId}_${message.streamId}`;
            if (processedTableIdsRef.current.has(sessionKey)) {
              console.log(`[v0] [RestaurantWS] Skipping duplicate table update: ${message.data.fbTableId}`);
              return;
            }
            processedTableIdsRef.current.add(sessionKey);

            // Cleanup old entries
            if (processedTableIdsRef.current.size > 500) {
              const entries = Array.from(processedTableIdsRef.current);
              // processedTableIdsRef.current = new Set(entries.slice(-500));
              processedTableIdsRef.current.clear();

              entries.slice(-500).forEach((entry) => {
                processedTableIdsRef.current.add(entry);
              });
            }
          }

          if (message?.data?.fbOrderId && message.type === "ORDER_UPSERT") {
            const orderKey = `${message?.data?.fbOrderId}_${message.streamId}`;
            if (processedTableIdsRef.current.has(orderKey)) {
              console.log(`[v0] [RestaurantWS] Skipping duplicate order: ${message?.data?.fbOrderId}`);
              return;
            }
            processedTableIdsRef.current.add(orderKey);

            // Cleanup old entries
            if (processedTableIdsRef.current.size > 500) {
              const entries = Array.from(processedTableIdsRef.current);
              // processedTableIdsRef.current = new Set(entries.slice(-500));
              processedTableIdsRef.current.clear();

              entries.slice(-500).forEach((entry) => {
                processedTableIdsRef.current.add(entry);
              });
            }
          }

          if ((message.type === "TABLE_STATUS_UPDATE" || message.type === "ORDER_UPSERT") && message.streamId) {
            const ackMsg = {
              type: "ACK",
              streamId: message.streamId,
              messageId: message.streamId,
              bunitId: message.bunitId,
              subscriptionType: "restaurant",
              deviceId: deviceId,
            };
            sendMessage(ackMsg);
            console.log("[v0] [RestaurantWS] ACK sent for stream ID:", message.streamId, "device:", deviceId);
          }

          if (message.type === "NEW_KOT" && message.streamId) {
            const ackMsg = {
              type: "ACK",
              streamId: message.streamId,
              messageId: message.streamId,
              bunitId: message.bunitId,
              subscriptionType: "restaurant",
              deviceId: deviceId,
            };
            sendMessage(ackMsg);
            console.log("[v0] [RestaurantWS] ACK sent for NEW_KOT stream ID:", message.streamId);
          }

          // setLastMessage(message);

          // if (callbacksRef.current.onMessage) {
          //   callbacksRef.current.onMessage(message);
          // } else {
          //   console.warn("[v0] [RestaurantWS] No onMessage callback registered!");
          // }
          const normalized = normalizeEvent(message);

          emitEvent(normalized.event, normalized.payload);

          if (normalized.event === "order:new") {
            console.log("[v0] Adding order to queue:", normalized.payload);

            setOrderQueue((prev) => [...prev, { data: normalized.payload }]);

            setOrderUpdateFlag((prev) => prev + 1);
          }

          if (normalized.event === "order:update") {
            console.log("[v0] Updating order:", normalized.payload);
            setOrderUpdateFlag((prev) => prev + 1);
          }

          setLastMessage(message);

          if (callbacksRef.current.onMessage) {
            callbacksRef.current.onMessage(message);
          }
        } catch (err) {
          console.error("[v0] [RestaurantWS] Error parsing message:", err);
          console.error("[v0] [RestaurantWS] Raw data:", event.data);
          setError(err);
        }
      };

      websocket.onerror = (err) => {
        console.error("[v0] [RestaurantWS] WebSocket error:", err);
        clearConnectionTimeout();
        isConnectingRef.current = false;
        setError(err);

        if (callbacksRef.current.onError) {
          callbacksRef.current.onError(err);
        }
      };

      websocket.onclose = (event) => {
        console.log("[v0] [RestaurantWS] WebSocket closed:", event.code, event.reason);

        clearConnectionTimeout();

        setConnected(false);
        isConnectingRef.current = false;
        stopHeartbeat();

        if (wsRef.current) {
          wsRef.current = null;
        }

        if (callbacksRef.current.onDisconnect) {
          callbacksRef.current.onDisconnect();
        }

        if (shouldReconnectRef.current && connectionAttemptRef.current < maxReconnectAttempts) {
          const attempt = connectionAttemptRef.current;
          const backoffDelay = Math.min(reconnectInterval * Math.pow(2, Math.min(attempt, 5)), 30000);

          log(`Reconnecting in ${backoffDelay}ms (attempt ${attempt + 1})`);

          setReconnecting(true);
          setReconnectAttempts(attempt + 1);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldReconnectRef.current && enabled && bunitId) {
              connectInternal();
            }
          }, backoffDelay);
        } else if (connectionAttemptRef.current >= maxReconnectAttempts) {
          console.error("[v0] [RestaurantWS] Max reconnection attempts reached");
        }
      };

      wsRef.current = websocket;
    } catch (err) {
      console.error("[v0] [RestaurantWS] Error creating WebSocket:", err);
      clearConnectionTimeout();
      isConnectingRef.current = false;
      setError(err);
      if (callbacksRef.current.onError) {
        callbacksRef.current.onError(err);
      }
    }
  }, [
    url,
    bunitId,
    deviceId,
    log,
    sendMessage,
    requestMissedEvents,
    processMessageQueue,
    startHeartbeat,
    saveStreamId,
    clearConnectionTimeout,
    clearHeartbeatTimeout,
    stopHeartbeat,
    maxReconnectAttempts,
    reconnectInterval,
    enabled,
    tillId,
  ]);

  const disconnectInternal = useCallback(() => {
    log("Disconnecting...");

    shouldReconnectRef.current = false;
    isConnectingRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    clearConnectionTimeout();
    stopHeartbeat();

    if (wsRef.current) {
      const ws = wsRef.current;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;

      try {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, "Client disconnect");
        }
      } catch (err) {
        console.error("[v0] Error closing WebSocket:", err);
      }

      wsRef.current = null;
    }

    setConnected(false);
    setReconnecting(false);
  }, [log, stopHeartbeat, clearConnectionTimeout]);

  const connect = useCallback(() => {
    shouldReconnectRef.current = true;
    connectionAttemptRef.current = 0;
    connectInternal();
  }, [connectInternal]);

  const disconnect = useCallback(() => {
    disconnectInternal();
  }, [disconnectInternal]);

  useEffect(() => {
    if (visibleOrders.length < MAX_VISIBLE_NOTIFICATIONS && orderQueue.length > 0) {
      const nextOrder = orderQueue[0];

      setVisibleOrders((prev) => [...prev, nextOrder]);

      setOrderQueue((prev) => prev.slice(1));
    }
  }, [orderQueue, visibleOrders]);

  const removeVisibleOrder = useCallback((orderId) => {
    setVisibleOrders((prev) => prev.filter((order) => order?.data?.orderId !== orderId));
  }, []);

  const removeVisibleOrderByExternalId = useCallback((externalId) => {
    setVisibleOrders((prev) => prev.filter((order) => order?.data?.externalId !== externalId));
  }, []);

  const reconnect = useCallback(() => {
    log("Manual reconnect requested");
    disconnectInternal();
    shouldReconnectRef.current = true;
    connectionAttemptRef.current = 0;
    setTimeout(() => connectInternal(), 100);
  }, [log, disconnectInternal, connectInternal]);

  useEffect(() => {
    if (!enabled || !bunitId || !url) {
      return;
    }

    console.log("[v0] [RestaurantWS] Initializing with device ID:", deviceId);

    loadLastStreamId();

    shouldReconnectRef.current = true;
    const timer = setTimeout(() => connectInternal(), 100);

    return () => {
      clearTimeout(timer);
      shouldReconnectRef.current = false;
      disconnectInternal();
    };
  }, [enabled, bunitId, deviceId, url, loadLastStreamId, connectInternal, disconnectInternal]);

  return {
    connected,
    reconnecting,
    reconnectAttempts,
    lastMessage,
    error,
    lastStreamId: lastStreamIdRef.current,
    deviceId,
    connect,
    disconnect,
    reconnect,
    sendMessage,
    visibleOrders,
    orderUpdateFlag,
    subscribe,
    removeVisibleOrder,
    removeVisibleOrderByExternalId,
  };
}

export default useRestaurantWebSocket;
